import './documentUploads.css';
import OptionalContractUpload from './OptionalContractUpload';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import config from '../../config';

import {
  fetchVenueTypes,
  fetchVenueTypeCategories,
  fetchSubCategories,
  fetchRules,
  fetchVenues,
  fetchFeatures,
  createVenue,
  updateVenue,
  deleteVenue,
  uploadImage,
  deleteVenueSpace,
  fetchVenueById,
  fetchSpaceTypes,
  fetchSpaceFeatures,
  fetchCuisineCategories,
  fetchMenus,
  createMenu,
  deleteMenu,
  createSpace,
  updateSpace,
  fetchSpaceCalendar,
  blockCalendarSlot,
  deleteCalendarEntry
} from '../../Apiservices/Auth/servers/venueServices';

import { GlassCard, GlassSection } from './GlassSurface';
import { GlassCtaButton } from './GlassCtaButton';
import { GlassInput } from './GlassInput';
import { GlassChip } from './GlassChip';
import { GlassSectionHeader } from './GlassSectionHeader';
import { GlassPageIndicator } from './GlassPageIndicator';
import { GlassTooltip } from './GlassTooltip';
import { GlassSpinner } from './GlassSpinner';
import { ImageUploadSection } from './ImageUploadSection';
import { LocationSection } from '../../component/LocationSection';
import { SectionDialog } from '../../component/SectionDialog';
import { LocationPickerDialog } from '../../component/LocationPickerDialog';
import { VenueCard } from '../../component/VenueCard';
import { VenueSpaceCalendar } from './VenueSpaceCalendar';
import { VenueSpaceForm } from './VenueSpaceForm';

import './VenueManageScreen.css';
import './glass-tokens.css';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Review Section Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ReviewSection = ({ 
  venueName, 
  address,
  spaces,
  selectedRules,
  existingImageIds,
  uploadedImageIds
}) => {
  return (
    <GlassSection>
      <h3 className="review-title">Review Your Venue</h3>
      <div className="review-items">
        <div className="review-item">
          <span className="review-label">Venue Name:</span>
          <span>{venueName}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Address:</span>
          <span>{address}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Spaces:</span>
          <span>{spaces.length} space(s)</span>
        </div>
        <div className="review-item">
          <span className="review-label">Rules:</span>
          <span>{selectedRules.length} rule(s)</span>
        </div>
        <div className="review-item">
          <span className="review-label">Images:</span>
          <span>{existingImageIds.length + uploadedImageIds.length} image(s)</span>
        </div>
      </div>
    </GlassSection>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Basic Info Section Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BasicInfoSection = ({ 
  venueName, 
  setVenueName, 
  description, 
  setDescription,
  errors = {}
}) => (
  <div className="form-section">
    <GlassInput
      label="Venue Name *"
      placeholder="Enter your venue name"
      value={venueName}
      onChange={setVenueName}
      error={errors.name}
    />
    <div className="section-gap" />
    <GlassInput
      label="Description"
      placeholder="Describe your venue (optional)"
      value={description}
      onChange={setDescription}
      multiline
      rows={3}
    />
  </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Rules Selection Section Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const RulesSelectionSection = ({ 
  allRules, 
  selectedRules, 
  onToggleRule,
  errors = {}
}) => (
  <GlassSection>
    <GlassSectionHeader title="Venue Rules *" />
    <p className="section-subtitle">Select rules that apply to your venue</p>
    {errors.rules && <div className="error-text">{errors.rules}</div>}
    <div className="rules-list-container">
      {allRules.length === 0 ? (
        <div className="empty-state">No rules available</div>
      ) : (
        <>
          <div className="select-all-row">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selectedRules.length === allRules.length}
                onChange={() => {
                  if (selectedRules.length === allRules.length) {
                    allRules.forEach(r => {
                      if (selectedRules.some(sr => sr.id === r.id)) {
                        onToggleRule(r);
                      }
                    });
                  } else {
                    allRules.forEach(r => {
                      if (!selectedRules.some(sr => sr.id === r.id)) {
                        onToggleRule(r);
                      }
                    });
                  }
                }}
              />
              <span>
                {selectedRules.length === allRules.length 
                  ? "Deselect All" 
                  : `Select All (${allRules.length})`}
              </span>
            </label>
          </div>
          <div className="rules-divider" />
          {allRules.map(rule => {
            const isSelected = selectedRules.some(r => r.id === rule.id);
            return (
              <label key={rule.id} className={`rule-item ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleRule(rule)}
                />
                <span className="rule-name">{rule.name}</span>
                {rule.description && (
                  <GlassTooltip content={rule.description}>
                    <span className="info-icon">ⓘ</span>
                  </GlassTooltip>
                )}
              </label>
            );
          })}
        </>
      )}
    </div>
  </GlassSection>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Navigation Buttons Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const NavigationButtons = ({ 
  currentPage, 
  totalPages, 
  onPrevious, 
  onNext, 
  onSubmit, 
  isFormValid,
  isEditingVenue,
  isSubmitting = false
}) => (
  <div className="nav-buttons">
    {currentPage > 0 && (
      <GlassCtaButton
        label="Previous"
        variant="secondary"
        onClick={onPrevious}
        fullWidth={false}
        style={{ flex: 1 }}
      />
    )}
    {currentPage > 0 && <div className="nav-spacer" />}
    <GlassCtaButton
      label={currentPage === totalPages - 1 
        ? (isEditingVenue ? "Update Venue" : "Add Venue") 
        : "Next"
      }
      icon={currentPage === totalPages - 1 ? null : "›"}
      onClick={currentPage < totalPages - 1 ? onNext : (isFormValid ? onSubmit : undefined)}
      disabled={currentPage === totalPages - 1 && !isFormValid}
      isLoading={isSubmitting}
    />
  </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Spaces Section Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SpacesSection = ({ 
  spaces, 
  setSpaces, 
  allFeatures,
  onOpenDialog,
  onDeleteSection,
  errors = {}
}) => (
  <GlassSection>
    <GlassSectionHeader 
      title="Venue Spaces *"
      trailing={
        <GlassCtaButton
          label="+ Add Space"
          variant="secondary"
          onClick={() => onOpenDialog()}
          fullWidth={false}
          style={{ 
            padding: '6px 12px', 
            fontSize: 'var(--glass-font-caption)', 
            minHeight: 'auto' 
          }}
        />
      }
    />
    {errors.spaces && <div className="error-text">{errors.spaces}</div>}
    {spaces.length === 0 ? (
      <div className="empty-state">
        <span className="empty-icon">📍</span>
        <span>No spaces added yet</span>
        <small>Click 'Add Space' to create venue spaces</small>
      </div>
    ) : (
      <div className="spaces-list">
        {spaces.map((section, index) => (
          <GlassCard key={index} inner>
            <div className="section-card-header">
              <span className="section-name">{section.name}</span>
              <div className="section-actions">
                <button 
                  className="icon-btn" 
                  onClick={() => onOpenDialog(index)}
                  aria-label="Edit section"
                >
                  ✏️
                </button>
                <button 
                  className="icon-btn delete" 
                  onClick={() => onDeleteSection(index)}
                  aria-label="Delete section"
                >
                  🗑️
                </button>
              </div>
            </div>
            <div className="section-stats">
              <GlassChip label={`📐 ${section.areaSqft} sqft`} />
              <GlassChip label={`👥 Min: ${section.minCapacity}`} />
              <GlassChip label={`👥 Max: ${section.maxCapacity}`} />
              <GlassChip 
                label={`💰 ₹${section.pricePerHour}/hr`} 
                variant="success" 
              />
              {section.offeredPrice && (
                <GlassChip 
                  label={`🏷️ ₹${section.offeredPrice}/hr`} 
                  variant="warning" 
                />
              )}
            </div>
            {section.featureIds && section.featureIds.length > 0 && (
              <div className="feature-chips">
                {section.featureIds.slice(0, 3).map(fid => {
                  const feature = allFeatures.find(f => f.id === fid);
                  return feature && (
                    <GlassChip key={fid} label={feature.name} />
                  );
                })}
                {section.featureIds.length > 3 && (
                  <span className="more-features">
                    +{section.featureIds.length - 3} more
                  </span>
                )}
              </div>
            )}
          </GlassCard>
        ))}
      </div>
    )}
  </GlassSection>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Venue Space Item Component (for expanded venue card)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const VenueSpaceItem = ({ 
  venue, 
  space, 
  onEditSpace, 
  onDeleteSpace,
  onViewCalendar
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  return (
    <div className="venue-space-item">
      <div className="venue-space-header">
        <div className="venue-space-info">
          <h4 className="venue-space-name">{space.name}</h4>
          <div className="venue-space-details">
            <GlassChip label={`👥 ${space.minCapacity || 0}-${space.maxCapacity || 0}`} />
            <GlassChip label={`📐 ${space.areaSqft || 0} sqft`} />
            <GlassChip 
              label={(() => {
                if (!space.pricing || space.pricing.length === 0) return '💰 No Price';
                const hour = space.pricing.find(p => p.type === 'per_hour');
                if (hour) return `💰 ₹${hour.price}/hr`;
                const person = space.pricing.find(p => p.type === 'per_person');
                if (person) return `💰 ₹${person.price}/person`;
                const day = space.pricing.find(p => p.type === 'per_day');
                if (day) return `💰 ₹${day.price}/day`;
                return '💰 No Price';
              })()} 
              variant="success" 
            />
          </div>
          {space.features && space.features.length > 0 && (
            <div className="venue-space-features">
              {space.features.slice(0, 3).map((feature, idx) => (
                <span key={idx} className="venue-space-feature">{feature.name}</span>
              ))}
              {space.features.length > 3 && (
                <span className="venue-space-feature-more">+{space.features.length - 3}</span>
              )}
            </div>
          )}
          {space.pricing && space.pricing.length > 0 && (
            <div className="venue-space-pricing">
              {space.pricing.map((pricing, idx) => (
                <span key={idx} className="venue-space-pricing-item">
                  {pricing.type === 'per_hour' && `🕐 ₹${pricing.price}/hr`}
                  {pricing.type === 'per_day' && `📅 ₹${pricing.price}/day`}
                  {pricing.type === 'per_person' && `👤 ₹${pricing.price}/person`}
                  {pricing.discountPrice && (
                    <span className="venue-space-discount">
                      (₹{pricing.discountPrice} after discount)
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {space.rules && space.rules.length > 0 && (
            <div className="venue-space-rules">
              <span className="venue-space-rules-label">Rules:</span>
              {space.rules.slice(0, 3).map((rule, idx) => (
                <span key={idx} className="venue-space-rule">{rule.name}</span>
              ))}
              {space.rules.length > 3 && (
                <span className="venue-space-rule-more">+{space.rules.length - 3}</span>
              )}
            </div>
          )}
        </div>
        <div className="venue-space-actions">
          <button 
            className="venue-space-action-btn"
            onClick={() => onViewCalendar(space)}
            aria-label="View calendar"
          >
            📅
          </button>
          <button 
            className="venue-space-action-btn"
            onClick={() => onEditSpace(space)}
            aria-label="Edit space"
          >
            ✏️
          </button>
          <button 
            className="venue-space-action-btn delete"
            onClick={() => onDeleteSpace(space)}
            aria-label="Delete space"
          >
            🗑️
          </button>
        </div>
      </div>
      
      {/* Calendar strip for this space */}
      {isCalendarOpen && venue.id && space.id && createPortal(
        <div className="calendar-dialog-overlay" onClick={() => setIsCalendarOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <VenueSpaceCalendar
              venueId={venue.id}
              spaceId={space.id}
              spaceName={space.name}
              onClose={() => setIsCalendarOpen(false)}
            />
          </div>
        </div>,
        document.body
      )}
      
      {/* Calendar toggle button */}
      <button 
        className="venue-space-calendar-toggle"
        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
      >
        <span>{isCalendarOpen ? 'Hide Calendar' : 'Show Calendar'}</span>
        <span className={`calendar-toggle-icon ${isCalendarOpen ? 'rotated' : ''}`}>▼</span>
      </button>
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CreateVenueForm Component (2 Steps - No Category Selection)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const CreateVenueForm = React.memo(({
  venueName, setVenueName,
  description, setDescription,
  uploadedImageIds, setUploadedImageIds,
  existingImageIds, setExistingImageIds,
  isUploadingImages, setIsUploadingImages,
  location, setLocation,
  address, setAddress,
  shortAddress, setShortAddress,
  fullAddress, setFullAddress,
  spaces, setSpaces,
  selectedRules, setSelectedRules,
  allRules,
  allFeatures,
  currentPage, setCurrentPage,
  isEditingVenue,
  isSubmitting,
  onReset,
  onSubmit,
  onImageUpload,
  onRemoveImage,
  onUploadPdf,
  uploadedPdfFile,
  contractFileId, setContractFileId, setIsUploadingContract,
  isUploadingPdf,
  errors = {},
  setErrors
}) => {
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [editingSectionData, setEditingSectionData] = useState(null);
  
  const totalPages = 2;

  const validateCurrentPage = useCallback(() => {
    const newErrors = {};
    
    switch (currentPage) {
      case 0:
        if (!venueName.trim()) {
          newErrors.name = "Venue name is required";
        }
        if (!location.lat || !location.lng) {
          newErrors.location = "Please select a location";
        }
        if (uploadedImageIds.length === 0 && existingImageIds.length === 0) {
          newErrors.images = "At least one image is required";
        }
        break;
      case 1:
        if (selectedRules.length === 0) {
          newErrors.rules = "Select at least one rule";
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentPage, venueName, location, uploadedImageIds, existingImageIds, selectedRules, setErrors]);

  const handleNext = useCallback(() => {
    if (validateCurrentPage()) {
      setCurrentPage(prev => prev + 1);
    }
  }, [validateCurrentPage, setCurrentPage]);

  const handlePrevious = useCallback(() => {
    setCurrentPage(prev => prev - 1);
  }, [setCurrentPage]);

  const handleOpenSectionDialog = useCallback((index = null) => {
    if (index !== null) {
      const section = spaces[index];
      setEditingSectionIndex(index);
      setEditingSectionData(section);
    } else {
      setEditingSectionIndex(null);
      setEditingSectionData(null);
    }
    setShowSectionDialog(true);
  }, [spaces]);

  const handleCloseSectionDialog = useCallback(() => {
    setShowSectionDialog(false);
    setEditingSectionIndex(null);
    setEditingSectionData(null);
  }, []);

  const handleSaveSection = useCallback((sectionData) => {
    if (editingSectionIndex !== null) {
      const updatedSpaces = [...spaces];
      updatedSpaces[editingSectionIndex] = sectionData;
      setSpaces(updatedSpaces);
    } else {
      setSpaces([...spaces, sectionData]);
    }
    setErrors(prev => ({ ...prev, spaces: undefined }));
    handleCloseSectionDialog();
  }, [spaces, setSpaces, editingSectionIndex, handleCloseSectionDialog, setErrors]);

  const deleteSection = useCallback((index) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      const updatedSpaces = [...spaces];
      updatedSpaces.splice(index, 1);
      setSpaces(updatedSpaces);
    }
  }, [spaces, setSpaces]);

  const isFormValid = React.useMemo(() => {
    return venueName.trim() !== '' &&
      location.lat !== 0 && location.lng !== 0 &&
      (uploadedImageIds.length > 0 || existingImageIds.length > 0) &&
      selectedRules.length > 0;
  }, [venueName, location, uploadedImageIds, existingImageIds, selectedRules]);

  const handleLocationSelect = useCallback((locationData) => {
    setLocation(locationData.location);
    setAddress(locationData.address);
    setShortAddress(locationData.shortAddress || locationData.city || '');
    setFullAddress(locationData.address || '');
    setErrors(prev => ({ ...prev, location: undefined }));
  }, [setLocation, setAddress, setShortAddress, setFullAddress, setErrors]);

  return (
    <div className="create-form-container">
      <div className="form-header">
        <div className="form-header-left">
          <button 
            className="back-btn" 
            onClick={() => {
              if (window.confirm("Are you sure you want to close creating venue?")) {
                onReset();
              }
            }}
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
          <span className="form-title">
            {isEditingVenue ? "Edit Venue" : "Add Venue"}
          </span>
        </div>
        <span className="form-step-counter">
          Step {currentPage + 1} of {totalPages}
        </span>
      </div>

      {/* Progress Bar under header */}
      <div className="header-progress-bar">
        {[...Array(totalPages)].map((_, index) => (
          <div
            key={index}
            className={`progress-segment ${currentPage >= index ? 'active' : ''}`}
          />
        ))}
      </div>

      <div className="form-content">
        <GlassSection className="form-card">
          
          {/* Step 1: Basic Info + Location + Address + Images */}
          {currentPage === 0 && (
            <>
              <BasicInfoSection 
                venueName={venueName}
                setVenueName={setVenueName}
                description={description}
                setDescription={setDescription}
                errors={errors}
              />
              
              <LocationSection 
                address={address}
                location={location}
                onOpenPicker={() => setShowLocationPicker(true)}
                error={errors.location}
              />
              
              <div className="form-section">
                <GlassInput
                  label="Short Address"
                  placeholder="e.g. Andheri West, Mumbai"
                  value={shortAddress}
                  onChange={setShortAddress}
                />
                <div className="section-gap" />
                <GlassInput
                  label="Full Address"
                  placeholder="Complete address with pincode"
                  value={fullAddress}
                  onChange={setFullAddress}
                  multiline
                  rows={2}
                />
              </div>

              <ImageUploadSection 
                label="Venue Images"
                required={true}
                existingImageIds={existingImageIds}
                uploadedImageIds={uploadedImageIds}
                onImageUpload={onImageUpload}
                onRemoveImage={onRemoveImage}
                isUploading={isUploadingImages}
                error={errors.images}
              />
              
              <OptionalContractUpload fileId={contractFileId} onChange={setContractFileId} onUploadingChange={setIsUploadingContract} />
              {/* PDF Upload */}
              <div className="form-section">
                <label className="section-label">Terms & Conditions (PDF)</label>
                {uploadedPdfFile ? (
                  <div className="pdf-uploaded">
                    <span>✅ Document uploaded</span>
                    <button onClick={onUploadPdf} className="pdf-remove-btn">
                      Remove
                    </button>
                  </div>
                ) : (
                  <button 
                    className="document-upload-button"
                    onClick={onUploadPdf}
                    disabled={isUploadingPdf}
                  >
                    {isUploadingPdf ? 'Uploading...' : 'Upload PDF'}
                  </button>
                )}
              </div>
            </>
          )}
          
          {/* Step 2: Rules */}
          {currentPage === 1 && (
            <RulesSelectionSection 
              allRules={allRules}
              selectedRules={selectedRules}
              onToggleRule={(rule) => {
                setSelectedRules(prev => 
                  prev.some(r => r.id === rule.id)
                    ? prev.filter(r => r.id !== rule.id)
                    : [...prev, rule]
                );
                setErrors(prev => ({ ...prev, rules: undefined }));
              }}
              errors={errors}
            />
          )}
          
          <NavigationButtons 
            currentPage={currentPage}
            totalPages={totalPages}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSubmit={onSubmit}
            isFormValid={isFormValid}
            isEditingVenue={isEditingVenue}
            isSubmitting={isSubmitting}
          />
        </GlassSection>
      </div>
      
      <SectionDialog
        isOpen={showSectionDialog}
        onClose={handleCloseSectionDialog}
        onSave={handleSaveSection}
        editingIndex={editingSectionIndex}
        initialData={editingSectionData}
        allFeatures={allFeatures}
      />
      
      <LocationPickerDialog
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialAddress={address}
        initialLocation={location}
      />
    </div>
  );
});

CreateVenueForm.displayName = 'CreateVenueForm';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// VenueList Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// VenueList Component (FIXED)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const VenueList = ({ 
  venueData, 
  onAddVenue,
  onEditVenue,
  onEditSpace,
  onDeleteVenue,
  onDeleteSpace,
  onViewCalendar,
  onAddSpace,
  isLoading = false,
  expandedVenueIds,
  onToggleExpand
}) => {
  if (isLoading) {
    return (
      <div className="loading-container">
        <GlassSpinner size={40} />
        <span>Loading venues...</span>
      </div>
    );
  }

  if (venueData.length === 0) {
    return (
      <div className="empty-venue-state">
        <div className="empty-venue-content">
          <div className="empty-image-placeholder">🏢</div>
          <h2>No Venues Yet</h2>
          <p>Register your venue and start getting bookings</p>
          <GlassCtaButton
            label="+ Add Venue"
            onClick={onAddVenue}
            style={{ 
              maxWidth: '220px', 
              minHeight: '46px', 
              fontSize: '15px', 
              borderRadius: '24px', 
              margin: '0 auto' 
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="venue-list-container">
      <div className="venue-list-header">
        <h2>My Venues ({venueData.length})</h2>
        <GlassCtaButton
          label="+ Add Venue"
          onClick={onAddVenue}
          fullWidth={false}
          style={{
            padding: '8px 20px',
            fontSize: '13px',
            minHeight: '38px',
            borderRadius: '19px'
          }}
        />
      </div>
      
      <div className="venue-grid">
        {venueData.map(venue => {
          const isExpanded = expandedVenueIds.has(venue.id);
          const hasSpaces = venue.spaces && venue.spaces.length > 0;

          console.log(`${config.API_BASE_URL}gallery/download/${venue.images[0].fileId}?isDownload=false`);
          
          return (
            <GlassCard key={venue.id} className="venue-card-full">
              {/* ─── Venue Header ─── */}
              <div className="venue-card-header-compact">
                <div className="venue-card-image-small">
                  {venue.images && venue.images.length > 0 ? (
                    <img 
                      src={`${config.API_BASE_URL}/gallery/download/${venue.images[0].fileId}?isDownload=false`} 
                      alt={venue.name}
                      className="venue-thumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '🏢';
                      }}
                    />
                  ) : (
                    <span className="venue-thumbnail-placeholder">🏢</span>
                  )}
                </div>
                <div className="venue-card-info">
                  <h3 className="venue-name">{venue.name}</h3>
                  {venue.description && (
                    <p className="venue-description">{venue.description}</p>
                  )}
                  <div className="venue-address">
                    <span>📍</span>
                    <span>{venue.shortAddress || venue.address}</span>
                  </div>
                  <div className="venue-stats">
                    <GlassChip label={`🪑 ${venue.spcae?.length || 0} spaces`} />
                    <GlassChip label={`📋 ${venue.rules?.length || 0} rules`} />
                    <GlassChip label={`🖼️ ${venue.images?.length || 0} images`} />
                  </div>
                </div>
                <div className="venue-card-actions">
                  <button 
                    className="venue-action-btn"
                    onClick={() => onEditVenue(venue)}
                    aria-label="Edit venue"
                  >
                    ✏️
                  </button>
                  <button 
                    className="venue-action-btn delete"
                    onClick={() => onDeleteVenue(venue.id)}
                    aria-label="Delete venue"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* ─── Expand/Collapse Toggle ─── */}
              <div className="venue-card-expand-section">
                {hasSpaces ? (
                  // When there are spaces, show the expand/collapse toggle and Add Space side-by-side
                  <div className="venue-expand-footer-flex">
                    <button 
                      className="expand-toggle-left"
                      onClick={() => onToggleExpand(venue.id)}
                      aria-expanded={isExpanded}
                    >
                      <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>▼</span>
                      <span>
                        {isExpanded 
                          ? `Hide ${venue.spaces.length} Space${venue.spaces.length !== 1 ? 's' : ''}`
                          : `Show ${venue.spaces.length} Space${venue.spaces.length !== 1 ? 's' : ''}`
                        }
                      </span>
                    </button>
                    <button
                      className="add-space-pill-right"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSpace(venue);
                      }}
                    >
                      + Add Space
                    </button>
                  </div>
                ) : (
                  // When there are NO spaces, show the centered "(+) Add Space" footer block
                  <div className="no-spaces-action-centered">
                    <button
                      className="add-space-centered-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSpace(venue);
                      }}
                    >
                      <span className="plus-icon-circle">+</span> Add Space
                    </button>
                  </div>
                )}
              </div>

              {/* ─── Expanded Spaces ─── */}
              {isExpanded && hasSpaces && (
                <div className="venue-spaces-container">
                  <div className="venue-spaces-divider" />
                  {venue.spaces.map((space) => (
                    <VenueSpaceItem
                      key={space.id}
                      venue={venue}
                      space={space}
                      onEditSpace={() => onEditSpace(venue, space)}
                      onDeleteSpace={() => onDeleteSpace(venue, space)}
                      onViewCalendar={() => onViewCalendar(venue, space)}
                    />
                  ))}
                  
                  {/* Add Space button at bottom of expanded section */}
                  <div className="venue-add-space-bottom">
                    <GlassCtaButton
                      label="+ Add Space"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSpace(venue);
                      }}
                      fullWidth={true}
                      style={{ 
                        padding: '8px 24px', 
                        fontSize: '13px', 
                        minHeight: '36px',
                        borderRadius: '18px'
                      }}
                    />
                  </div>
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main VenueManageScreen Component (Side Tabs Removed)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const VenueManageScreen = () => {
  // Data state
  const [allRules, setAllRules] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [allSpaceTypes, setAllSpaceTypes] = useState([]);
  const [allCuisineCategories, setAllCuisineCategories] = useState([]);
  const [allMenus, setAllMenus] = useState([]);
  const [venueData, setVenueData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedVenueIds, setExpandedVenueIds] = useState(new Set());
  const [isCreatingVenue, setIsCreatingVenue] = useState(false);
  const [isEditingVenue, setIsEditingVenue] = useState(false);
  const [editingVenueId, setEditingVenueId] = useState(null);
  const [isSpaceFormOpen, setIsSpaceFormOpen] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  const [selectedVenueForSpace, setSelectedVenueForSpace] = useState(null);

  // Form state
  const [venueName, setVenueName] = useState('');
  const [description, setDescription] = useState('');
  const [uploadedImageIds, setUploadedImageIds] = useState([]);
  const [existingImageIds, setExistingImageIds] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [location, setLocation] = useState({ lat: 0, lng: 0 });
  const [address, setAddress] = useState('');
  const [shortAddress, setShortAddress] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [spaces, setSpaces] = useState([]);
  const [selectedRules, setSelectedRules] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [errors, setErrors] = useState({});
  const [contractFileId, setContractFileId] = useState(null);
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [uploadedPdfFile, setUploadedPdfFile] = useState('');
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  // ─── Reset Form ──────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setVenueName('');
    setDescription('');
    setUploadedImageIds([]);
    setExistingImageIds([]);
    setLocation({ lat: 0, lng: 0 });
    setAddress('');
    setShortAddress('');
    setFullAddress('');
    setSpaces([]);
    setSelectedRules([]);
    setErrors({});
    setCurrentPage(0);
    setEditingSpace(null);
    setUploadedPdfFile('');
    setContractFileId(null);
    setIsSubmitting(false);
    setIsUploadingImages(false);
    setIsUploadingPdf(false);
  }, []);

  // ─── Load Data ─────────────────────────────────────────────────

  const loadFeatures = useCallback(async () => {
    try {
      const features = await fetchFeatures();
      setAllFeatures(features || []);
    } catch (err) {
      console.error("Error loading features:", err);
    }
  }, []);

  const loadSpaceTypes = useCallback(async () => {
    try {
      const types = await fetchSpaceTypes();
      setAllSpaceTypes(types || []);
    } catch (err) {
      console.error("Error loading space types:", err);
    }
  }, []);

  const loadCuisineCategories = useCallback(async () => {
    try {
      const categories = await fetchCuisineCategories();
      setAllCuisineCategories(categories || []);
    } catch (err) {
      console.error("Error loading cuisine categories:", err);
    }
  }, []);

  const loadMenus = useCallback(async () => {
    try {
      const menus = await fetchMenus();
      setAllMenus(menus || []);
    } catch (err) {
      console.error("Error loading menus:", err);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const [rules, venues] = await Promise.all([
        fetchRules(),
        fetchVenues()
      ]);
      
      setAllRules(rules || []);
      setVenueData(venues || []);
      
      await Promise.all([
        loadFeatures(),
        loadSpaceTypes(),
        loadCuisineCategories(),
        loadMenus()
      ]);
    } catch (err) {
      setFetchError(err.message || "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [loadFeatures, loadSpaceTypes, loadCuisineCategories, loadMenus]);

  // ─── Effects ──────────────────────────────────────────────────

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // ─── Handlers ─────────────────────────────────────────────────

  const handleImageUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingImages(true);
    const newIds = [];
    for (const file of files) {
      try {
        const id = await uploadImage(file);
        if (id) {
          newIds.push(id);
        }
      } catch (err) {
        console.error("Error uploading image:", err);
      }
    }
    setUploadedImageIds(prev => [...prev, ...newIds]);
    setIsUploadingImages(false);
    e.target.value = null;
  }, []);

  const removeImage = useCallback((id, isExisting) => {
    if (isExisting) {
      setExistingImageIds(prev => prev.filter(imgId => imgId !== id));
    } else {
      setUploadedImageIds(prev => prev.filter(imgId => imgId !== id));
    }
  }, []);

  const handlePdfUpload = useCallback(async () => {
    if (uploadedPdfFile) {
      setUploadedPdfFile('');
      return;
    }
    
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setIsUploadingPdf(true);
        try {
          const id = await uploadImage(file);
          if (id) {
            setUploadedPdfFile(id);
          }
        } catch (err) {
          console.error("Error uploading PDF:", err);
        } finally {
          setIsUploadingPdf(false);
        }
      };
      input.click();
    } catch (err) {
      console.error("Error picking PDF:", err);
    }
  }, [uploadedPdfFile]);

  // ─── Venue CRUD ──────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (isUploadingContract) return;
    const newErrors = {};
    if (!venueName.trim()) newErrors.name = "Venue name is required";
    if (!location.lat || !location.lng) newErrors.location = "Please select a location";
    if (uploadedImageIds.length === 0 && existingImageIds.length === 0) {
      newErrors.images = "At least one image is required";
    }
    if (selectedRules.length === 0) newErrors.rules = "Select at least one rule";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const allImageIds = [...existingImageIds, ...uploadedImageIds];
      
      const payload = {
        name: venueName.trim(),
        description: description.trim() || undefined,
        shortAddress: shortAddress.trim(),
        fullAddress: fullAddress.trim(),
        latitude: location.lat,
        longitude: location.lng,
        imageIds: allImageIds,
        ruleIds: selectedRules.map(r => r.id),
        termsFileId: uploadedPdfFile || undefined,
        contractFileId,
      };

      if (isEditingVenue && editingVenueId) {
        await updateVenue(editingVenueId, payload);
        alert("Venue updated successfully!");
      } else {
        await createVenue(payload);
        alert("Venue added successfully!");
      }

      resetForm();
      setIsCreatingVenue(false);
      setIsEditingVenue(false);
      setEditingVenueId(null);
      setCurrentPage(0);
      await loadInitialData();
    } catch (err) {
      console.error("Error submitting venue:", err);
      alert(`Error: ${err.message || "Failed to save venue"}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    venueName, description, shortAddress, fullAddress, location, 
    uploadedImageIds, existingImageIds, selectedRules, uploadedPdfFile, contractFileId, isUploadingContract,
    isEditingVenue, editingVenueId, loadInitialData, resetForm
  ]);

  const handleAddVenue = useCallback(() => {
    resetForm();
    setIsCreatingVenue(true);
    setIsEditingVenue(false);
    setEditingVenueId(null);
    setCurrentPage(0);
  }, [resetForm]);

  const handleEditVenue = useCallback((venue) => {
    resetForm();
    setIsCreatingVenue(true);
    setIsEditingVenue(true);
    setEditingVenueId(venue.id);
    setCurrentPage(0);
    
    setVenueName(venue.name || '');
    setDescription(venue.description || '');
    setLocation({
      lat: parseFloat(venue.latitude) || 0,
      lng: parseFloat(venue.longitude) || 0
    });
    setAddress(venue.fullAddress || venue.address || '');
    setShortAddress(venue.shortAddress || '');
    setFullAddress(venue.fullAddress || venue.address || '');
    setUploadedPdfFile(venue.termsFileId || '');
    setContractFileId(venue.contractFileId || null);
    
    const imageIds = (venue.images || [])
      .map(img => {
        const urlParts = img.url?.split('/');
        return urlParts?.[urlParts.length - 1] || '';
      })
      .filter(id => id);
    setExistingImageIds(imageIds);
    
    setSpaces((venue.spaces || []).map(section => ({
      name: section.name || '',
      venueTypeId: section.venueTypeId || 0,
      areaSqft: parseInt(section.areaSqft) || 0,
      minCapacity: section.minCapacity || 0,
      maxCapacity: section.maxCapacity || 0,
      pricePerHour: parseFloat(section.pricePerHour) || 0,
      offeredPrice: section.offeredPrice ? parseFloat(section.offeredPrice) : null,
      featureIds: section.featureIds || [],
      images: (section.images || []).map(img => ({ 
        url: img.url, 
        mediaType: "image", 
        isPrimary: img.isPrimary 
      }))
    })));
    
    setSelectedRules([...(venue.rules || [])]);
  }, [resetForm]);

  const handleDeleteVenue = useCallback(async (venueId) => {
    if (!window.confirm("Are you sure you want to delete this venue? All spaces will be removed too.")) return;
    
    try {
      await deleteVenue(venueId);
      await loadInitialData();
      alert("Venue deleted successfully");
    } catch (err) {
      console.error("Error deleting venue:", err);
      alert("Failed to delete venue");
    }
  }, [loadInitialData]);

  // ─── Space CRUD ──────────────────────────────────────────────

  const handleAddSpace = useCallback((venue) => {
    setSelectedVenueForSpace(venue);
    setEditingSpace(null);
    setIsSpaceFormOpen(true);
  }, []);

  const handleEditSpace = useCallback((venue, space) => {
    setSelectedVenueForSpace(venue);
    setEditingSpace(space);
    setIsSpaceFormOpen(true);
  }, []);

  const handleDeleteSpace = useCallback(async (venue, space) => {
    if (!window.confirm(`Delete "${space.name}" from ${venue.name}?`)) return;
    
    try {
      await deleteVenueSpace(venue.id, space.id);
      const updatedVenue = await fetchVenueById(venue.id);
      if (updatedVenue) {
        setVenueData(prev => prev.map(v => 
          v.id === venue.id ? updatedVenue : v
        ));
      }
      alert("Space deleted successfully");
    } catch (err) {
      console.error("Error deleting space:", err);
      alert("Failed to delete space");
    }
  }, []);

  const handleCreateMenu = useCallback(async (menuData) => {
    try {
      const result = await createMenu(menuData);
      if (result) {
        setAllMenus(prev => [...prev, result]);
        return result;
      }
    } catch (error) {
      console.error("Error creating menu inside screen:", error);
      throw error;
    }
  }, []);

  const handleDeleteMenu = useCallback(async (menuId) => {
    try {
      const success = await deleteMenu(menuId);
      if (success) {
        setAllMenus(prev => prev.filter(m => m.id !== menuId));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error deleting menu inside screen:", error);
      throw error;
    }
  }, []);

  const handleSpaceFormClose = useCallback(() => {
    setIsSpaceFormOpen(false);
    setSelectedVenueForSpace(null);
    setEditingSpace(null);
  }, []);

  const handleSpaceFormSubmit = useCallback(async () => {
    await loadInitialData();
    setIsSpaceFormOpen(false);
    setSelectedVenueForSpace(null);
    setEditingSpace(null);
  }, [loadInitialData]);

  const handleViewCalendar = useCallback((venue, space) => {
    // Open calendar dialog for the space
    console.log(`View calendar for ${space.name} in ${venue.name}`);
  }, []);

  // ─── Expand/Collapse ─────────────────────────────────────────

  const handleToggleExpand = useCallback((venueId) => {
    setExpandedVenueIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(venueId)) {
        newSet.delete(venueId);
      } else {
        newSet.add(venueId);
      }
      return newSet;
    });
  }, []);

  // Auto-expand if only 1 venue
  useEffect(() => {
    if (venueData.length === 1 && venueData[0]?.id) {
      setExpandedVenueIds(new Set([venueData[0].id]));
    }
  }, [venueData]);

  // ─── Render ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="loading-container">
        <GlassSpinner size={48} />
        <span>Loading venues...</span>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="error-container">
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <h3>Something went wrong</h3>
          <p>{fetchError}</p>
          <GlassCtaButton
            label="Retry"
            onClick={loadInitialData}
            style={{ maxWidth: '200px', margin: '0 auto' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="venue-manage-screen">
      <div className="main-content-full">
        {isCreatingVenue ? (
          <CreateVenueForm
            venueName={venueName}
            setVenueName={setVenueName}
            description={description}
            setDescription={setDescription}
            uploadedImageIds={uploadedImageIds}
            setUploadedImageIds={setUploadedImageIds}
            existingImageIds={existingImageIds}
            setExistingImageIds={setExistingImageIds}
            isUploadingImages={isUploadingImages}
            setIsUploadingImages={setIsUploadingImages}
            location={location}
            setLocation={setLocation}
            address={address}
            setAddress={setAddress}
            shortAddress={shortAddress}
            setShortAddress={setShortAddress}
            fullAddress={fullAddress}
            setFullAddress={setFullAddress}
            spaces={spaces}
            setSpaces={setSpaces}
            selectedRules={selectedRules}
            setSelectedRules={setSelectedRules}
            allRules={allRules}
            allFeatures={allFeatures}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isEditingVenue={isEditingVenue}
            isSubmitting={isSubmitting || isUploadingContract}
            contractFileId={contractFileId}
            setContractFileId={setContractFileId}
            setIsUploadingContract={setIsUploadingContract}
            uploadedPdfFile={uploadedPdfFile}
            isUploadingPdf={isUploadingPdf}
            onReset={() => {
              resetForm();
              setIsCreatingVenue(false);
              setIsEditingVenue(false);
              setEditingVenueId(null);
              setCurrentPage(0);
              setEditingSpace(null);
            }}
            onSubmit={handleSubmit}
            onImageUpload={handleImageUpload}
            onRemoveImage={removeImage}
            onUploadPdf={handlePdfUpload}
            errors={errors}
            setErrors={setErrors}
          />
        ) : (
          <VenueList 
            venueData={venueData}
            onAddVenue={handleAddVenue}
            onEditVenue={handleEditVenue}
            onEditSpace={handleEditSpace}
            onDeleteVenue={handleDeleteVenue}
            onDeleteSpace={handleDeleteSpace}
            onViewCalendar={handleViewCalendar}
            onAddSpace={handleAddSpace}
            expandedVenueIds={expandedVenueIds}
            onToggleExpand={handleToggleExpand}
          />
        )}
      </div>
      
      {/* Space Form Dialog */}
      {isSpaceFormOpen && selectedVenueForSpace && (
        <VenueSpaceForm
          venue={selectedVenueForSpace}
          editingSpace={editingSpace}
          onClose={handleSpaceFormClose}
          onSubmit={handleSpaceFormSubmit}
          allSpaceTypes={allSpaceTypes}
          allRules={allRules}
          allFeatures={allFeatures}
          allCuisineCategories={allCuisineCategories}
          allMenus={allMenus}
          onCreateMenu={handleCreateMenu}
          onDeleteMenu={handleDeleteMenu}
        />
      )}
    </div>
  );
};

export default VenueManageScreen;
